import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from '../Dashboard/index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { addNewOrganization, editOrganization, getAllOrganizations } from "../../../redux/features/organization/organizationService";
import { showToast } from "../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark } from "@fortawesome/free-regular-svg-icons";
import Modal from "../../../components/Modal";
import Pagination from "../../../components/Pagination";
import NoResultsFound from "../../../components/NoResultsFound";
import TableSkeleton from "../../../components/Skeleton";
import { editUser } from "../../../redux/features/users/userService";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import SearchBarOnChange from "../../../components/SearchBarOnChange";

const Organization = () => {

    const dispatch = useDispatch();
    const roleDropdownRef = useRef(null);

    const { isOrganizationsLoading, pageCount, organizations } = useSelector((state) => state.organization.allOrganizations);
    const { isOrganizationCreating } = useSelector((state) => state.organization.newOrganization);
    const { isEditingOrganization } = useSelector((state) => state.organization.editedOrganization);

    const [formData, setFormData] = useState({
        name: '',
    });

    const [errorMessages, setErrorMessages] = useState({});
    const [disabledBtn, setBtnDisable] = useState(false);
    const [skip, setSkip] = useState(0);

    const limit = 10;  // Number of items per page
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [initialOrganization, setInitialOrganization] = useState(null);
    const [editErrorMsgs, setEditErrorMsgs] = useState({
        name: '',
    });

    const [disabledEditBtn, setEditBtnDisable] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchAllOrganizations = useCallback(() => {
        dispatch(getAllOrganizations({
            skip: skip,
            limit,
            search: searchQuery,
        }));
    }, [dispatch, skip, searchQuery]);

    useEffect(() => {
        fetchAllOrganizations();
    }, [fetchAllOrganizations]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrorMessages((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    // Validate input
    const isValidOrganization = () => {
        const errors = {};
        const trimmedName = formData.name.trim();
        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (Object.keys(errors).length > 0) {
            setErrorMessages(errors);
            return false;
        } else {
            return true;
        }
    };

    const insertNewOrganization = async () => {
        if (isValidOrganization()) {
            setBtnDisable(true);
            try {
                const response = await dispatch(addNewOrganization(formData)).unwrap();
                setBtnDisable(false);
                setFormData({
                    name: '',
                });
                if (roleDropdownRef.current) {
                    roleDropdownRef.current.clearSelection();
                }
                fetchAllOrganizations();
                showToast('success', response.message);
            }
            catch (error) {
                setBtnDisable(false);
                showToast('error', error.message);
            };
        }
    };

    // Edit organization
    const handleSelectedOrganizationChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null);
        setSelectedOrganization((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEditErrorMsgs((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const isValidEditOrganization = () => {
        const errors = {};
        const trimmedName = selectedOrganization.name.trim();

        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (Object.keys(errors).length > 0) {
            setEditErrorMsgs(errors);
            return false;
        } else {
            return true;
        }
    };

    function getChangedValues(selectedData) {
        const changedValues = {};
        for (const key in selectedData) {
            if (
                key !== 'id' &&
                selectedData.hasOwnProperty(key) &&
                selectedData[key] !== initialOrganization[key]
            ) {
                changedValues[key] = selectedData[key];
            }

            if (key === 'id') {
                changedValues[key] = selectedData[key];
            }
        }
        return changedValues;
    }

    const editOrganizationDetails = async () => {
        if (isValidEditOrganization(selectedOrganization)) {
            const data = getChangedValues(selectedOrganization);
            const hasChanges = Object.keys(data).some(key => key !== 'id');
            if (!hasChanges) {
                setErrorMsg('There are no changes to update.');
            } else {
                setEditBtnDisable(true);
                try {
                    const response = await dispatch(editOrganization(data)).unwrap();
                    setEditBtnDisable(false);
                    setSelectedOrganization(null);
                    setInitialOrganization(null);
                    setModalOpen(false);
                    showToast('success', response.message);
                    fetchAllOrganizations();
                }
                catch (error) {
                    setEditBtnDisable(false);
                    showToast('error', error.message);
                };
            }
        }
    };

    //Activate Organization
    const [isActivateModalOpen, setActivateModalOpen] = useState(false);
    const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
    const [selectedOrganizationId, setSelectedOrganizationId] = useState(null);

    const closeModal = () => setActivateModalOpen(false);
    const closDeactivationModal = () => setDeactivationModalOpen(false);

    const handleYes = async () => {
        setActivateModalOpen(false);
        try {
            const response = await dispatch(editOrganization({ id: selectedOrganizationId, status: true })).unwrap();
            setSelectedOrganizationId(null);
            setInitialOrganization(null);
            showToast('success', response.message);
            fetchAllOrganizations();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }
    const handleRejectYes = async () => {
        setDeactivationModalOpen(false);
        try {
            const response = await dispatch(editOrganization({ id: selectedOrganizationId, status: false })).unwrap();
            setSelectedOrganizationId(null);
            setInitialOrganization(null);
            showToast('success', response.message);
            fetchAllOrganizations();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    const handlePageChange = (pageNumber) => {
        setSkip(pageNumber - 1);
    };

    const handleSearch = (query) => {
        setSearchQuery(query)
        setSkip(0);
    };

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add New Organization</h1>
                <div className={styles.formContainer}>
                    <form
                        className={styles.coordinatorForm}
                        noValidate
                    >
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='name' className={`form-label`}>
                                        Name<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='name'
                                        name='name'
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder='Enter Name'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.name && (
                                        <p className={`text-danger`}>{errorMessages.name}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            className={`btn-primary`}
                            type='button'
                            disabled={disabledBtn}
                            onClick={insertNewOrganization}
                        >
                            {isOrganizationCreating ? <BeatLoader color='#ffffff' /> : `Create`}
                        </button>
                    </form>
                </div>
            </div>
            <div className={`search-card`}>
                <div className={`search-card-header`}>
                    <h1 className={`form-title`}>Organizations</h1>
                    <SearchBarOnChange
                        value={searchQuery}
                        onSearch={handleSearch}
                    />
                </div>
                <div className={`search-card-body`}>
                    {isOrganizationsLoading ?
                        <TableSkeleton columns={["Name", "Status", "Actions"]} rowCount={10} height={20} />
                        : organizations.length > 0 ?
                            <div className={`table-container`}>
                                <table className={`table`}>
                                    <thead className={`thead`}>
                                        <tr>
                                            <th className={`theadColumnOne`}>Name</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`tbody`}>
                                        {organizations?.map((organization) => (
                                            <tr key={organization.id}>
                                                <td className={`tdataColumnOne`}>
                                                    {organization.name}
                                                </td>
                                                <td>{organization.status ? 'Active' : 'Inactive'}</td>
                                                <td className={`actionsBtn`}>
                                                    <button
                                                        className={organization.status ? `btn-inactive` : `btn-active`}
                                                        onClick={() => {
                                                            setSelectedOrganizationId(organization.id);
                                                            organization.status ? setDeactivationModalOpen(true) : setActivateModalOpen(true);
                                                        }
                                                        }>{organization.status ? 'Deactivate' : 'Acitvate'}</button>
                                                    <button
                                                        className={`btn-active`}
                                                        onClick={() => {
                                                            setSelectedOrganization(organization);
                                                            setInitialOrganization(organization);
                                                            setModalOpen(true);
                                                            setEditErrorMsgs({
                                                                name: ''
                                                            })
                                                        }
                                                        }>Edit</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            :
                            <NoResultsFound />
                    }
                </div>
                <div className={`search-card-footer`}>
                    {pageCount > 0 && (
                        <div className={styles.paginationContainer}>
                            <Pagination
                                currentPage={skip + 1}
                                totalPages={pageCount}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isModalOpen &&
                <div className={`modal-overlay`}>
                    <div className={`modal-card`}>
                        <div className={`modal-header`}>
                            <h4 className={`modal-title`}>Edit Course Type</h4>
                            <FontAwesomeIcon icon={faXmark} onClick={() => setModalOpen(false)} size="lg" cursor="pointer" />
                        </div>
                        <div className={`modal-body`}>
                            <div className={styles.formContainer}>
                                <form
                                    className={styles.coordinatorForm}
                                    noValidate
                                >
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Name<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='name'
                                                name='name'
                                                value={selectedOrganization.name || ''}
                                                onChange={handleSelectedOrganizationChange}
                                                placeholder='Enter Name'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.name && (
                                                <p className={`text-danger`}>{editErrorMsgs.name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        className={`btn-primary`}
                                        type='button'
                                        disabled={disabledEditBtn}
                                        onClick={editOrganizationDetails}
                                    >
                                        {isEditingOrganization ? <BeatLoader vehicleType='#ffffff' /> : `Save`}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {isActivateModalOpen && (
                <Modal
                    title='Course Category Activation'
                    onClose={closeModal}
                    onYes={handleYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to activate the Course Category?`}
                    colorType={`var(--green)`}
                />
            )}
            {isDeactivationModalOpen && (
                <Modal
                    title='Course Category Deactivation'
                    onClose={closDeactivationModal}
                    onYes={handleRejectYes}
                    isInput={false}
                    iconName={faCircleXmark}
                    bodyText={`Are you sure you want to deactivate the Course Category?`}
                    colorType={`var(--danger)`}
                />
            )}
        </div>
    );
};

export default Organization;